import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Zap, ArrowRight, Eye, EyeOff } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail.trim();
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function statusFallback(code) {
  if (code === 400) return "Invalid request. Check your inputs and try again.";
  if (code === 401) return "Invalid email or password.";
  if (code === 403) return "You don't have permission to do that.";
  if (code === 409) return "An account with this email already exists.";
  if (code === 422) return "Some fields are invalid. Please review and try again.";
  if (code === 429) return "Too many login attempts. Please wait a few minutes and try again.";
  if (code >= 500) return `Server error (${code}). Please try again in a moment.`;
  return `Request failed (${code}).`;
}

function buildAuthErrorMessage(err) {
  // CONTRACT:
  //   If the server returned ANY response (err.response present), surface a message
  //   derived from the response body or status code — never a "network error".
  //   Only when the request truly never reached the server do we say "Network error".

  if (err?.response) {
    const data = err.response.data;

    // 1a. FastAPI shape: { detail: "..." } or { detail: [...] }
    const fromDetail = formatApiErrorDetail(data?.detail);
    if (fromDetail) return fromDetail;

    // 1b. Plain string body
    if (typeof data === "string" && data.trim()) return data.trim();

    // 1c. Alt schema: { message: "..." } or { error: "..." }
    if (data && typeof data.message === "string" && data.message.trim()) return data.message.trim();
    if (data && typeof data.error === "string" && data.error.trim()) return data.error.trim();

    // 1d. Status code fallback when body has nothing useful
    return statusFallback(err.response.status);
  }

  // 2. No response object — request never reached the server (CORS rejected,
  //    DNS failure, offline, timeout, etc.).
  if (err?.code === "ECONNABORTED") return "The request timed out. Please try again.";
  return "Network error. Check your connection and try again.";
}

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation that matches backend expectations
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }
    if (mode === 'register' && (password.length < 8 || !/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/\d/.test(password))) {
      setError('Password must be at least 8 characters and include uppercase, lowercase, and a number.');
      return;
    }
    if (mode === 'register' && !name.trim()) {
      setError('Please enter your name.');
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await login(trimmedEmail, password);
      } else {
        await register(trimmedEmail, password, name.trim());
      }
      // Honor ?redirect=/path if present and safe (relative path only — no protocol-relative or external URLs)
      const target = searchParams.get('redirect');
      const safe = target && target.startsWith('/') && !target.startsWith('//') ? target : '/';
      navigate(safe, { replace: true });
    } catch (err) {
      setError(buildAuthErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#141414] flex items-center justify-center relative overflow-hidden">
      {/* Background texture */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1754738381783-f9a2847bfef2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBwdXJwbGUlMjB0ZWNobm9sb2d5JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzUwMjExNDF8MA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="absolute inset-0 bg-[#141414]/85 backdrop-blur-2xl" />

      <div className="relative z-10 w-full max-w-md mx-4">
        {/* Logo */}
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-[#7B2FFF] rounded-md flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="text-2xl font-bold tracking-tight text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            HighConnect
          </span>
        </div>

        {/* Auth Card */}
        <div className="border border-[#27272A] rounded-lg bg-[#1A1A1A] p-8">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              {mode === 'login' ? 'Sign in' : 'Create account'}
            </h1>
            <p className="text-sm text-[#A1A1AA] mt-1">
              {mode === 'login'
                ? 'Connect your GHL account to AI automation'
                : 'Get started with HighConnect'}
            </p>
          </div>

          {error && (
            <div data-testid="auth-error" className="mb-4 p-3 border border-red-500/30 rounded-md bg-red-500/10 text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#A1A1AA] text-sm">Name</Label>
                <Input
                  data-testid="auth-name-input"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="bg-[#111111] border-[#27272A] text-[#EDEDF0] placeholder:text-[#71717A] focus:border-[#7B2FFF] focus:ring-[#7B2FFF] rounded-md"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#A1A1AA] text-sm">Email</Label>
              <Input
                data-testid="auth-email-input"
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                className="bg-[#111111] border-[#27272A] text-[#EDEDF0] placeholder:text-[#71717A] focus:border-[#7B2FFF] focus:ring-[#7B2FFF] rounded-md"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#A1A1AA] text-sm">Password</Label>
              <div className="relative">
                <Input
                  data-testid="auth-password-input"
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 8 chars · upper, lower, number"
                  required
                  className="bg-[#111111] border-[#27272A] text-[#EDEDF0] placeholder:text-[#71717A] focus:border-[#7B2FFF] focus:ring-[#7B2FFF] rounded-md pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#EDEDF0] transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {mode === 'register' && (
              <div className="flex items-start gap-2.5">
                <input
                  data-testid="agree-terms-checkbox"
                  type="checkbox"
                  id="agree-terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-[#27272A] bg-[#111111] text-[#7B2FFF] focus:ring-[#7B2FFF] accent-[#7B2FFF] shrink-0"
                />
                <label htmlFor="agree-terms" className="text-xs text-[#A1A1AA] leading-relaxed">
                  I agree to the{' '}
                  <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-[#7B2FFF] hover:text-[#9353FF] underline">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#7B2FFF] hover:text-[#9353FF] underline">
                    Privacy Policy
                  </a>
                </label>
              </div>
            )}

            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading || (mode === 'register' && !agreedToTerms)}
              className="w-full bg-[#7B2FFF] hover:bg-[#9353FF] text-white font-semibold rounded-md h-11 transition-all duration-200 hover:shadow-[0_0_20px_rgba(123,47,255,0.3)] active:scale-[0.98]"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <span className="flex items-center gap-2">
                  {mode === 'login' ? 'Sign in' : 'Create account'}
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 pt-4 border-t border-[#27272A] text-center">
            <button
              data-testid="auth-toggle-mode"
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
              className="text-sm text-[#A1A1AA] hover:text-[#7B2FFF] transition-colors"
            >
              {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>

        {/* Page footer (legal links) */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-xs text-[#71717A]" data-testid="auth-footer">
          <span>© 2025 HighConnect</span>
          <span aria-hidden="true">·</span>
          <Link to="/privacy" className="hover:text-[#EDEDF0] transition-colors" data-testid="auth-footer-privacy">
            Privacy Policy
          </Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms" className="hover:text-[#EDEDF0] transition-colors" data-testid="auth-footer-terms">
            Terms of Service
          </Link>
        </div>
      </div>
    </div>
  );
}
